/**
  * @class meleSinput
  * @description
  *
  * # Purpose
  * Implements `<mele-sinput>` custom web element.
  *
  * # Use
  * Create element in HTML:
  * ```
  * parentEl.innerHTML += '<mele-sinput></mele-sinput>';
  * ```
  * Create element in JavaScript:
  * ```
  * parentEl.appendChildren({
  *   tag: 'mele-sinput',
  *   ... other attributes ...
  * })
  * ```
  *
  * # Implementation notes
  *
  */

import meleElement from 'meleElement'
const moment = require('moment')

const PROPERTIES = _.extend({}, {
  tabindex: { type: 'number', description: 'https://developer.mozilla.org/en-US/docs/Web/HTML/Global_attributes/tabindex', default: 1 },
  placeholder: { type: 'string', description: 'default text when item is empty' },
  value: { type: 'string', description: 'value of input' },
  label: { type: 'string', description: 'input label' },
  validation: { type: 'array', description: "validation array of options for input. accepted format of items in array is 'string', or a 'string:value' i.e ['required','min:2', 'max:5']", readonly: true },
  invalid: { type: 'boolean', description: 'checks if input content is valid based' },
  readonly: { type: 'boolean', description: 'checks if is contenteditable', default: false },
  preselect: { type: 'boolean', description: 'preselectes all content from input', default: true },
  eventValueChanged: { type: 'function', description: 'Called when value change; if not set, will publish mmValueChanged message to parent module' },
  eventFocusChanged: { type: 'function', description: 'Called when input focused' },
  eventEnterPressed: { type: 'function', description: 'Called when enterkey pressed' },
}, meleElement._properties)

export default class meleSinput extends meleElement {
  ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  /**
  * Creates internal DOM structure.
  */
  init() {
    super.init()
    this._value = this.createElementFast('mele-value')
    this._label = this.createElementFast('mele-label')

    this.appendChild(this._value)
    this.appendChild(this._label)

    this.readonlyChanged(this.readonly)
    this.preselectChanged(this.readonly)
    this.checkValidity()
    this.onkeydown = (e)=>{
      if (e.which === 13) {
        e.preventDefault()
        if(typeof this.eventEnterPressed==='function') {
          this.eventEnterPressed(this)
        }
        e.stopPropagation()
      }
    }
    this.onkeyup = (e)=>{
      if (e.which === 13) {
        e.preventDefault()
        e.stopPropagation()
      } else {
        this.setProperty('value', e.target.textContent, true)
      }
    }

    this.onfocus = (e)=>{
      if(this.preselect) {
        const selection = window.getSelection()
        const range = document.createRange()
        range.selectNodeContents(e.target)
        selection.removeAllRanges()
        selection.addRange(range)
      }
      if(typeof this.eventFocusChanged==='function') {
        this.eventFocusChanged(this)
      }
    }

    this.onclick = ()=>{
      if(typeof this.eventFocusChanged==='function') {
        this.eventFocusChanged(this)
      }
    }
  } // init

  preselectChanged(value) {
  }

  labelChanged(value, old) {
    const html = this.translateAndSetDependencies(value, false, 'label')
    this._label.innerHTML = html
  }

  valueChanged(value, old, from_input) {
    this.checkValidity(value)
    if(!from_input && value != old) {
      this._value.innerHTML = value
    }
    if(typeof this.eventValueChanged==='function') {
      this.eventValueChanged(this, value, old, from_input)
    }
  }

  readonlyChanged(value) {
    if(value) {
      this.removeAttribute('contenteditable')
    } else {
      this.setAttribute('contenteditable', '')
    }
  }

  checkValidity(value = '') {
    let invalid = false
    const RULES = {
      // required: (v,l)=> !_.isNil(v) && v != '',
      digits: (v, l)=> v.length ==l && !_.isNaN(v),
      date_format: (v, l)=> moment(v, l).isValid(),
    }
    const validation = this.validation || []

    invalid = validation.some(item => {
      const [rule, limit] = item.split(/\:(.*)/)
      const res = RULES[rule](value, limit)
      return !res
    })

    this.invalid = invalid

    const holder = this.closest('.mele-container:not([propagate])')
    if(holder && typeof (holder.formStatusChanged)==='function') {
      //holder.formStatusChanged(this);
    }
  }
} // class meleSinput

meleSinput._properties = PROPERTIES
meleSinput.observedAttributes = _.keys(PROPERTIES)

window.customElements.define('mele-sinput', meleSinput)
